import React, { useState, useEffect } from 'react';
import { progressService, WeeklyProgressResponse } from '../../services/progressService';
import { useTranslation } from '../../i18n';
import MaterialSymbol from '../common/MaterialSymbol';

/**
 * "Diese Woche" — Wochenfortschritt im neuen Design (.week-*, .progress-line).
 * Re-Skin von components/Progress/WeeklyProgressWidget: gleicher Datenabruf
 * (progressService.getWeeklyProgress), neue Optik aus design-system.css.
 */
const WeekWidget: React.FC = () => {
    const { t } = useTranslation();
    const [data, setData] = useState<WeeklyProgressResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadProgress = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await progressService.getWeeklyProgress();
                setData(response);
            } catch (err) {
                console.error('Error loading weekly progress:', err);
                setError(t('progress.errorLoading'));
            } finally {
                setLoading(false);
            }
        };
        loadProgress();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatDateRange = (start: string, end: string) => {
        const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
        return `${new Date(start).toLocaleDateString('de-DE', options)} – ${new Date(end).toLocaleDateString('de-DE', options)}`;
    };

    const getDayLabel = (day: string) => {
        const dayMap: Record<string, string> = {
            'Mon': t('progress.days.mon'),
            'Tue': t('progress.days.tue'),
            'Wed': t('progress.days.wed'),
            'Thu': t('progress.days.thu'),
            'Fri': t('progress.days.fri'),
            'Sat': t('progress.days.sat'),
            'Sun': t('progress.days.sun'),
        };
        return dayMap[day] || day;
    };

    const formatChange = (change: number) => (change > 0 ? `+${change}` : `${change}`);

    if (loading) {
        return (
            <div className="panel">
                <div className="skel" />
                <div className="skel" />
                <div className="skel" />
            </div>
        );
    }

    if (error || !data) {
        return null;
    }

    return (
        <div className="panel">
            <div className="sec-head">
                <h2 className="sec-title">
                    <span className="ico"><MaterialSymbol name="calendar_month" size={19} /></span>
                    {t('progress.weeklyTitle')}
                </h2>
                <span className="date-range">{formatDateRange(data.currentWeek.startDate, data.currentWeek.endDate)}</span>
            </div>

            <div className="week-top">
                <div className="week-cell">
                    <div className="big">{data.currentWeek.lessonsCompleted}</div>
                    <div className="cap">{t('progress.lessonsCompleted')}</div>
                    <div className="goal">
                        <MaterialSymbol name="arrow_forward" size={14} />
                        <span className="pill">{formatChange(data.comparison.lessonsChange)}</span>
                    </div>
                </div>
                <div className="week-cell">
                    <div className="big">{data.currentWeek.coursesCompleted}</div>
                    <div className="cap">{t('progress.coursesCompleted')}</div>
                    <div className="goal">
                        <MaterialSymbol name="arrow_forward" size={14} />
                        <span className="pill">{formatChange(data.comparison.coursesChange)}</span>
                    </div>
                </div>
                <div className="week-cell">
                    <div className="big amber">{data.currentWeek.activeDays}/7</div>
                    <div className="cap">{t('progress.activeDays')}</div>
                </div>
            </div>

            <div className="daily-label">{t('progress.dailyActivity')}</div>
            <div className="days">
                {data.dailyProgress.map((day, index) => {
                    const isToday = new Date(day.date).toDateString() === new Date().toDateString();
                    const hasActivity = day.lessonsCompleted > 0;
                    const className = `day${isToday ? ' active' : ''}${hasActivity ? ' done' : ''}`;
                    return (
                        <div key={index} className={className} title={`${day.lessonsCompleted} ${t('progress.lessons')}`}>
                            {getDayLabel(day.day)}
                        </div>
                    );
                })}
            </div>

            <div className="progress-line">
                <div className="head">
                    <span>{t('progress.overallProgress')}</span>
                    <span><b>{data.totalStats.totalCompleted}/{data.totalStats.totalEnrolled} {t('progress.coursesLabel')}</b></span>
                </div>
                <div className="bar"><i style={{ width: `${data.totalStats.completionRate}%` }} /></div>
                <div className="foot">
                    <span><MaterialSymbol name="hourglass_top" size={15} />{data.totalStats.inProgress} {t('progress.inProgress')}</span>
                    <span><MaterialSymbol name="check_circle" size={15} />{data.totalStats.completionRate}% {t('progress.complete')}</span>
                </div>
            </div>
        </div>
    );
};

export default WeekWidget;
